// Analysis-page Scores engine (buildAnnualScores): the card's chart series, forward-estimate
// years, the metrics-fed Growth plumbing (revenue and EBITDA series), and the Analysis-only
// helpers (company value, net debt, Rule of 40). The Quality and Growth RULES (constants,
// pillars, the per-year Quality engine, the Growth per-year rule) come from ./scoreCore, a
// GENERATED copy of funviz-amplify's resources/screener-compute/lib/scoreCore.mjs. Do not
// edit scoreCore.js here; change scoreCore.mjs in funviz-amplify and run its
// scripts/sync-score-core.sh. This module re-exports scoreCore so existing importers keep
// working. Spec: docs/specs/SCORE_CARDS.md §1.6.

import { lastNonNullPoint, stepJoin } from '../hooks/seriesMath';
import {
  dropAfterLastPrice,
  fiscalYearNumber,
  forwardFiscalYears,
  netMarginFromRow,
  reportedFiscalYears,
  scaleTotalSeries,
  statementTimestamp,
  yoyGrowth,
} from './annualGrowth';
import {
  isFiniteNumber,
  mean,
  round,
  isPosFinite,
  ebitdaMarginFrom,
  ocfMarginFrom,
  QUALITY_AVERAGE_WINDOWS,
  computeYearQuality,
  averageQualityWindow,
  GROWTH_HEADLINE_WINDOW,
  GROWTH_AVERAGE_WINDOWS,
  GROWTH_NON_POSITIVE_REVENUE_CAP,
  isGrowthLender,
  lenderNetRevenue,
  priorPeak,
  growthPeakFactor,
  risingYearShare,
  growthTrendFactor,
  growthYearScore,
  buildQualityRows,
  operatingRoaByDate,
  qualityHeadlines,
} from './scoreCore';

export * from './scoreCore';
































































































































/**
 * Classic Rule of 40 = EBITDA margin + revenue YoY. Floored at 0, no ceiling.
 * Not a quality pillar, and since 2026-11-02 not what the Growth score plots
 * (see `growthYearScore`): the `ruleOf40Total`/`PerShare` series keep the
 * plain reading for reference. Null when either input is missing.
 *
 * The margin term is an EBITDA margin, which is Brad Feld's original basis.
 * Reported years use the provider's GAAP-derived `ebitda` (EBIT + D&A, so stock
 * comp is still an expense). Forward years do NOT use the EBITDA consensus,
 * which is an analyst adjusted figure on a different basis — they hold this
 * margin flat (see `buildAnnualScores`). Net margin is still the quality
 * score's profitability input — only this term moved.
 */
export const ruleOf40 = (margin, revenueGrowth) => {
  if (!isFiniteNumber(margin) || !isFiniteNumber(revenueGrowth)) return null;
  return Math.max(0, margin + revenueGrowth);
};































/**
 * Strict trailing `n`-year mean of per-year growth scores, the screener's
 * averageGrowthWindow: each point's 0-100 score (2 dp, recovered exactly from
 * the stored fraction), averaged, capped at GROWTH_NON_POSITIVE_REVENUE_CAP
 * when a point's revenue or the first point's base-year revenue is not
 * positive, rounded to 2 dp, returned as a fraction. Null unless the last `n`
 * points are `n` consecutive fiscal years.
 */
const trailingGrowthAverage = (points, n) => {
  if (!points || points.length < n) return null;
  const window = points.slice(-n);
  if (window[n - 1].year - window[0].year !== n - 1) return null;
  let average = mean(window.map((point) => round(point.y * 100, 2)));
  const revenues = [window[0].baseRevenue, ...window.map((point) => point.revenue)];
  if (revenues.some((value) => !(value > 0))) average = Math.min(average, GROWTH_NON_POSITIVE_REVENUE_CAP);
  return round(average, 2) / 100;
};

/** Growth-line tooltip: the strict 5Y/10Y `trailingGrowthAverage`s ending at the last point, "—" when not covered. */
/** Analysis-side per-year context: sector flags plus this statement set's operating ROA by date. */
const qualityCtxOf = (qualityInputs) => ({
  isFinancial: !!qualityInputs?.isFinancial,
  isReit: !!qualityInputs?.isReit,
  industry: typeof qualityInputs?.industry === 'string' ? qualityInputs.industry : null,
  operatingRoaByDate: operatingRoaByDate(qualityInputs?.income, qualityInputs?.balance),
});

/** Statement rows (scoreCore's buildQualityRows) with the chart timestamp `x` added, in the same order. */
const qualityRowsWithX = (qualityInputs) => {
  const incomeRows = (qualityInputs?.income || []).filter((r) => r && typeof r.date === 'string');
  return buildQualityRows(qualityInputs || {}).map((row, i) => ({ ...row, x: statementTimestamp(incomeRows[i]) }));
};

/**
 * The three headline Quality scores (0-100, or null) for the latest reported
 * fiscal year: strict 3/5/10-year averages. Same values the screener stores as
 * qualityScore / qualityScore5y / qualityScore10y.
 */
export function buildQualityHeadlines(qualityInputs = {}) {
  const rows = buildQualityRows(qualityInputs);
  if (rows.length < 2) return { qualityScore: null, qualityScore5y: null, qualityScore10y: null };
  return qualityHeadlines(rows, qualityCtxOf(qualityInputs));
}

export const formatGrowthAverages = (points) => GROWTH_AVERAGE_WINDOWS
  .map((n) => {
    const avg = trailingGrowthAverage(points, n);
    return `${n}Y avg ${avg == null ? '—' : (avg * 100).toFixed(2)}`;
  })
  .join(' · ');

/** True when `series` has an observation at or before `ts`. Later points do not count. */
const hasObservationAtOrBefore = (series, ts) => (
  (series || []).some((point) => point && point[0] != null && point[0] <= ts)
);

/**
 * True when `series` has points but every one of them is LATER than `ts`, so
 * `stepJoin` would hand back its earliest point — a future print written
 * backward. An empty series is NOT this case: `stepJoin` yields null there,
 * which the callers already treat as a gap.
 */
const borrowsFutureObservation = (series, ts) => (
  !!series?.length && !hasObservationAtOrBefore(series, ts)
);

/**
 * Latest finite value at or before `ts` (step-hold forward). A fiscal year
 * before the first observation is null — `stepJoin` would otherwise fall
 * back to the earliest later point, and this path does not.
 */
const joinedNumber = (series, ts) => {
  if (!series?.length || ts == null || !hasObservationAtOrBefore(series, ts)) return null;
  const value = stepJoin([[ts, null]], series)[0][1];
  if (!isFiniteNumber(value)) return null;
  return value;
};

/**
 * Price timestamp `buildActualSeries` would hold at `ts`: the latest price
 * at or before `ts`. Null when every price is later — do not return price[0].
 */
const priceTimestampAt = (price, ts) => {
  let sourceTs = null;
  (price || []).forEach((point) => {
    if (!point || point[0] == null) return;
    const t = point[0];
    if (t <= ts && (sourceTs == null || t > sourceTs)) sourceTs = t;
  });
  return sourceTs;
};

/**
 * Company value at a fiscal-year date: market cap (price × shares), or
 * enterprise value (market cap + net debt). No price at or before the year, or
 * a non-positive value, is null — a gap, not 0, and not a later price written
 * backward.
 *
 * The same rule applies to every input, not just price: `stepJoin` falls back
 * to a series' EARLIEST point for any timestamp before it, so a fiscal year
 * before the first share-count print (or, on enterprise value, before the first
 * net-debt print) would otherwise be scored on a later print written backward.
 * Both are gapped instead.
 *
 * Price is the only input read at the price point it holds (`priceTimestampAt`,
 * via `joinedNumber`'s step-hold); shares and net debt are read at the fiscal
 * year end `ts` ITSELF. They are dated on the report date, while price is a
 * daily trading series, so whenever a fiscal year ends on a non-trading day the
 * held price sits one or two days earlier than the share/net-debt print dated
 * exactly on `ts` (a Dec-31 on a weekend, or any 52/53-week retailer closing on
 * a Saturday). Measuring shares against that earlier price timestamp would read
 * a same-year print as a future one and gap the whole year. `netDebtAtValue`
 * reads the same `ts`, so the leverage ratio's numerator and denominator agree
 * about which observations exist.
 */
export const companyValueAt = (price, shares, netDebt, valueBasis, ts) => {
  const priceValue = joinedNumber(price, ts);
  const shareCount = joinedNumber(shares, ts);
  if (priceValue == null || shareCount == null) return null;
  if (valueBasis === 'enterpriseValue' && borrowsFutureObservation(netDebt, ts)) return null;
  // No net-debt series at all keeps the old behaviour of market cap (null added
  // as 0); a series whose prints are all later than `ts` is the gap above.
  const debt = valueBasis === 'enterpriseValue' ? (joinedNumber(netDebt, ts) ?? 0) : 0;
  const value = priceValue * shareCount + debt;
  if (!isFiniteNumber(value) || !(value > 0)) return null;
  return value;
};

/**
 * Net debt inside that company value: the debt printed at the fiscal year end,
 * the same observation `companyValueAt` adds into enterprise value, so the
 * leverage ratio's numerator matches its denominator. Gated on a price existing
 * at or before the year, since there is no company value without one. Null
 * stays null (not coerced to 0).
 */
export const netDebtAtValue = (price, netDebt, ts) => {
  if (priceTimestampAt(price, ts) == null) return null;
  return joinedNumber(netDebt, ts);
};

const FY_MATCH_MS = 10 * 24 * 60 * 60 * 1000;

/**
 * Value of `series` at a fiscal-year timestamp. An exact timestamp wins,
 * including an explicit null (a missing year, not a nearby fill). Otherwise
 * the nearest finite point within 10 days — wide enough for a date-rounding
 * slip, narrow enough that a TTM bridge a quarter later is not a fiscal year.
 */
export const sampleNear = (series, ts) => {
  if (!series?.length || ts == null) return null;
  let exact = null;
  let foundExact = false;
  let best = null;
  let bestDist = Infinity;
  series.forEach((point) => {
    if (!point || point[0] == null) return;
    const [t, value] = point;
    if (t === ts) {
      exact = value;
      foundExact = true;
    }
    if (value == null || !isFiniteNumber(value)) return;
    const dist = Math.abs(t - ts);
    if (dist < bestDist) {
      bestDist = dist;
      best = value;
    }
  });
  if (foundExact) return isFiniteNumber(exact) ? exact : null;
  if (bestDist > FY_MATCH_MS) return null;
  return best;
};

const MIN_YEAR_GAP_MS = 270 * 24 * 60 * 60 * 1000;

/** Fallback fiscal-year spine when the income statement isn't on the payload. */
const fiscalYearsFromSeries = (series, lastPriceTs) => {
  const filtered = dropAfterLastPrice(series, lastPriceTs)
    .filter((point) => isFiniteNumber(point[1]))
    .sort((a, b) => a[0] - b[0]);
  const kept = [];
  filtered.forEach(([t]) => {
    if (!kept.length || t - kept[kept.length - 1].x >= MIN_YEAR_GAP_MS) {
      kept.push({ year: fiscalYearNumber(null, t), x: t, row: null });
    }
  });
  return kept;
};

const consecutive = (years, index) => (
  index > 0 && years[index].year === years[index - 1].year + 1
);

/**
 * Annual scores and the rates behind them, in fractions.
 *
 * `valueBasis` moves company value, which since 2026-10-29 drives exactly ONE
 * thing: the "Net debt / company value" UNDERLYING display line
 * (`netDebtToValue`) and its label. It no longer moves the LEVERAGE PILLAR —
 * that pillar switched to FCF coverage (SCORE_CARDS.md §1.6) and takes no
 * price input — and it no longer gates whether a year scores at all: the
 * `row.companyValue == null` gate that used to drop a whole year's quality
 * point was removed once nothing in the pillars read a company value. Before
 * that removal, flipping the toggle to Enterprise Value could still make
 * quality points disappear (`companyValueAt` returns null under
 * `enterpriseValue` for a net-debt series whose prints are all later than the
 * year), which contradicted the "no price input" claim. The quality total is
 * now genuinely unaffected by the Market Cap / Enterprise Value toggle.
 * `valueBasis` never moved net margin, ROA, ROIC, or revenue growth, before or
 * after any of this.
 *
 * ONE price dependency remains, and it is not the toggle's. `netDebtAtValue`
 * returns null unless a price exists at or before the fiscal year, so a year
 * with no price gets a null `netDebt` and therefore a null LEVERAGE pillar.
 * The year still scores — on the other four pillars — rather than vanishing,
 * which is the behaviour the removed gate was hiding. (The dependency is a
 * deliberate "numerator and denominator agree about which observations exist"
 * rule inherited from the old price-derived pillar; narrowing it is a separate
 * question, not a copy fix.)
 *
 * `displayScale` is presentation only: it moves the plotted `revenueGrowth`
 * line and nothing else. The QUALITY score is computed on the TOTAL-company
 * basis whichever way it is set. Per-share growth has already netted out
 * dilution, and dilution has its own dedicated pillar, so feeding per-share
 * growth into the quality pillars charged the same dilution twice and made the
 * score swing with a display toggle. Do not re-wire `displayScale` into the
 * quality math.
 *
 * Rule of 40 is emitted twice instead, on a total and a per-share revenue
 * basis, as two independently named series rather than one toggle-driven line:
 * the per-share lens is growth net of dilution, which is a deliberate second
 * reading and not the definition of the metric.
 *
 * Forward estimates. `years` stays the REPORTED fiscal-year spine and every
 * reported point is computed exactly as before; `forwardYears` is a separate
 * spine whose points are appended to the series the payload can project on the
 * SAME basis as their reported history: both revenue-growth lines (analyst
 * revenue consensus), the EBITDA margin (held at its last reported level — the
 * EBITDA consensus is an adjusted figure on a different basis, see below) and,
 * from those two, both Rule-of-40 readings and both growth scores. Quality, net
 * margin, share-count growth, net debt / company value and FCF growth get no
 * forward point.
 *
 * Per-year Quality (the operating-margin guard, the FCF leverage window and the
 * rest of the pillar rules) comes from scoreCore; SCORE_CARDS.md §1.6.
 * Every per-fiscal-year profitability input goes through
 * `guardedProfitabilityMargin` before `profitabilityPillar` sees it; the
 * plotted `netMargin` UNDERLYING line stays on the raw, unguarded value —
 * the guard is a score input only. Every per-fiscal-year leverage input is
 * the 3-year mean FCF of the 3 most recent consecutive fiscal years ending
 * there when that window is fully available, falling back to the
 * single-year FCF otherwise (today's pre-2026-10-30 behaviour); the window
 * is fixed at 3 regardless of which trailing-average headline (3Y/5Y/10Y) is
 * later built from the resulting per-year score.
 *
 * The plotted `quality` series is ALSO new as of 2026-10-30: each point is
 * the trailing 3-year average of the (guarded, smoothed) per-year raw score,
 * not that raw score itself — null wherever the last 3 fiscal years are not
 * 3 consecutive years with a non-null raw score each. The raw score survives
 * on each point's `raw` field (pillars already set this precedent), and the
 * tooltip's 5Y/10Y figures are the strict averages of the per-year scores
 * ending at that point, never of the now-averaged plotted line.
 *
 * Growth (Total)/(Per Share) lines (2026-11-01; per-year rule 2026-11-02).
 * Like Quality, each plotted point is the strict trailing 3-year average of
 * the per-year growth score (`growthYearScore`: above-peak growth plus 0.75 ×
 * that year's margin clamped to −100%..40%, over 0.70, capped by the 15% growth ceiling; OCF
 * margin for a REIT), with the
 * per-year score on `raw` — the screener's growthScoreTotal/PerShare rule.
 * Reported points' tooltips show the 5Y/10Y averages of the reported per-year
 * scores only (the screener's growthScore*5y/10y basis); forward points
 * average across the reported/forward boundary and carry no `.detail`. See
 * `plotGrowth` below. A lender (`isGrowthLender`, 2026-11-03) scores on net
 * revenue with margin 0 and gets no forward Growth points.
 *
 * No TTM bridge point (2026-10-30, removed). A TTM "third spine" between
 * reported fiscal years and forward estimates was tried and then dropped:
 * its Quality reading omitted dilution, the FCF-quality pillar (with no
 * growth/level window to read, a profitable company's fcf pillar scores
 * `null`, not 0) and the revenue-quality pillar, which structurally capped
 * the TTM Quality score at `profitability.max + leverage.max` (0.60 on the
 * 2026-10-31 caps) versus 1.00 for a normal fiscal year — a mechanical dip at every
 * TTM point for any company whose FCF/revenue pillars normally contribute,
 * independent of the company's actual trajectory. Removed rather than
 * patched (see SCORE_CARDS.md §5 for the dated entry).
 */
export const buildAnnualScores = ({
  price = [],
  shares = [],
  netDebt = [],
  valueBasis = 'marketCap',
  displayScale = 'perShare',
  revenue = [],
  ebitda = [],
  fcf = [],
  incomeAnnual = [],
  roa = null,
  roic = null,
  // Raw statement rows the Quality score runs on (lambda.py's `qualityInputs`:
  // { income, cashflow, balance, isFinancial, isReit }, reporting currency,
  // oldest-first). Quality never reads the FX-converted series above.
  qualityInputs = null,
} = {}) => {
  const lastPriceTs = lastNonNullPoint(price)?.[0] ?? null;
  let years = reportedFiscalYears(incomeAnnual, lastPriceTs);
  if (!years.length) {
    years = fiscalYearsFromSeries(revenue, lastPriceTs);
    if (!years.length) years = fiscalYearsFromSeries(fcf, lastPriceTs);
  }

  const reportedRevenue = dropAfterLastPrice(revenue, lastPriceTs);
  // The plotted revenue-growth line follows the page's per-share/total toggle.
  const displayRevenue = scaleTotalSeries(reportedRevenue, displayScale, shares);
  // Quality's inputs are total-company regardless of that toggle (see above).
  // Rule of 40 reads both bases, so both are built unconditionally.
  const totalRevenue = scaleTotalSeries(reportedRevenue, 'total', shares);
  const perShareRevenue = scaleTotalSeries(reportedRevenue, 'perShare', shares);
  // EBITDA and revenue both come from `metrics.*`, so the EBITDA margin's
  // numerator and denominator share one FX basis (see `ebitdaMarginFrom`).
  const totalEbitda = scaleTotalSeries(dropAfterLastPrice(ebitda, lastPriceTs), 'total', shares);
  const totalFcf = scaleTotalSeries(dropAfterLastPrice(fcf, lastPriceTs), 'total', shares);

  // Raw statement rows (reporting currency) for Quality, and for a REIT's
  // Growth margin (OCF / revenue): both come from `qualityInputs`, so the
  // ratio obeys the same-source rule (§1.3).
  const qualityRows = qualityRowsWithX(qualityInputs);
  const isReit = !!qualityInputs?.isReit;
  const ocfMarginByYear = new Map(qualityRows
    .filter((r) => r.year != null)
    .map((r) => [r.year, ocfMarginFrom(r.operatingCashFlow, r.revenue)]));
  // Per-share Growth basis: revenue / income-statement weightedAverageShsOut,
  // both from `qualityInputs` — the screener's revenuePerShare — not revenue
  // over the page's `shares` series (a different share count).
  const revenuePerShareByYear = new Map(qualityRows
    .filter((r) => r.year != null)
    .map((r) => [r.year, isPosFinite(r.shares) && r.revenue != null ? r.revenue / r.shares : null]));
  // Lender Growth basis (see `isGrowthLender`): net revenue and net revenue per
  // share by year, from the same `qualityInputs` rows.
  const lender = isGrowthLender(qualityRows, qualityInputs?.isFinancial, isReit);
  const lenderNetByYear = new Map(lender ? qualityRows
    .filter((r) => r.year != null)
    .map((r) => [r.year, lenderNetRevenue(r)]) : []);
  const lenderNetAt = (year) => lenderNetByYear.get(year) ?? null;
  const lenderNetPerShareByYear = new Map(lender ? qualityRows
    .filter((r) => r.year != null)
    .map((r) => {
      const net = lenderNetRevenue(r);
      return [r.year, isPosFinite(r.shares) && net != null ? net / r.shares : null];
    }) : []);

  const rows = years.map((year) => {
    const margin = netMarginFromRow(year.row);
    const shareCount = joinedNumber(shares, year.x);
    const totalRevenueAt = sampleNear(totalRevenue, year.x);
    const ebitdaAt = sampleNear(totalEbitda, year.x);
    return {
      ...year,
      margin,
      ebitda: ebitdaAt,
      ebitdaMargin: ebitdaMarginFrom(ebitdaAt, totalRevenueAt),
      // The Growth score's margin term: that year's own margin (see GROWTH_MARGIN_CAP).
      growthMarginBasis: isReit ? (ocfMarginByYear.get(year.year) ?? null) : ebitdaMarginFrom(ebitdaAt, totalRevenueAt),
      displayRevenue: sampleNear(displayRevenue, year.x),
      revenue: totalRevenueAt,
      // Page basis (metrics revenue / `shares`): only the forward tail's first
      // growth denominator reads it, since the consensus revenue is on it too.
      perShareRevenue: sampleNear(perShareRevenue, year.x),
      // Reported per-share growth basis (see `revenuePerShareByYear`).
      growthRevenuePerShare: revenuePerShareByYear.get(year.year) ?? null,
      fcf: sampleNear(totalFcf, year.x),
      shares: shareCount,
      netDebt: netDebtAtValue(price, netDebt, year.x),
      companyValue: companyValueAt(price, shares, netDebt, valueBasis, year.x),
      roa: Array.isArray(roa) ? sampleNear(roa, year.x) : null,
      roic: Array.isArray(roic) ? sampleNear(roic, year.x) : null,
    };
  });

  const growthAt = (pick, index) => {
    if (!consecutive(years, index)) return null;
    return yoyGrowth(pick(rows[index]), pick(rows[index - 1]));
  };

  // Growth peak and rising-years input: total revenue per reported fiscal
  // year, extended by the forward tail further down (scoreCore priorPeak,
  // risingYearShare, frozen at the last reported year for forward years).
  const peakRevenue = rows.map((row) => (lender ? lenderNetAt(row.year) : row.revenue));
  const peakYears = years.map((year) => year.year);
  const peakConsecutiveAt = (k) => k > 0 && peakYears[k] != null && peakYears[k] === peakYears[k - 1] + 1;
  // The rising-years share is frozen at the last reported year for forward
  // years: an estimate carries no evidence of how reliable past growth was.
  const lastReportedIndex = rows.length - 1;
  // Per-year growth scores for one fiscal year (0-1 fractions, or null), the
  // screener's computeYearGrowthScores: Total takes the lower of total and
  // per-share growth; both share the total-revenue growth-credit factor.
  const yearGrowthScores = (index, gTotal, gPerShare, margin) => {
    const peakRows = peakRevenue.map((revenue, k) => ({ year: peakYears[k], revenue }));
    const peakOf = (r) => r.revenue;
    // A non-positive revenue in the year or its base year (exact 0 included)
    // scores 0 on both lines; a missing revenue stays a gap.
    const revenueNow = peakRevenue[index];
    const revenueBase = peakRevenue[index - 1];
    if (peakConsecutiveAt(index) && isFiniteNumber(revenueNow) && isFiniteNumber(revenueBase)
      && (revenueNow <= 0 || revenueBase <= 0)) {
      return { total: 0, perShare: 0 };
    }
    const peak = priorPeak(peakRows, index, peakOf);
    if (peak == null) return { total: null, perShare: null };
    const factor = growthPeakFactor(peakRevenue[index], peakRevenue[index - 1], peak)
      * growthTrendFactor(risingYearShare(peakRows, Math.min(index, lastReportedIndex), peakOf));
    const totalGrowth = isFiniteNumber(gTotal) && isFiniteNumber(gPerShare) ? Math.min(gTotal, gPerShare) : gTotal;
    const total = growthYearScore(totalGrowth, factor, margin);
    const perShare = growthYearScore(gPerShare, factor, margin);
    return { total: total == null ? null : total / 100, perShare: perShare == null ? null : perShare / 100 };
  };

  const netMargin = [];
  const ebitdaMargin = [];
  const revenueGrowth = [];
  const revenueGrowthTotal = [];
  const revenueGrowthPerShare = [];
  const shareGrowth = [];
  const netDebtToValue = [];
  const fcfGrowth = [];
  const roaPoints = [];
  const roicPoints = [];
  const ruleOf40Total = [];
  const ruleOf40PerShare = [];
  // Per-year growth scores (fractions), reported years then forward years;
  // the plotted `growthScoreTotal`/`growthScorePerShare` points are built
  // from these further down.
  const growthRawTotal = [];
  const growthRawPerShare = [];

  rows.forEach((row, index) => {
    const at = (y) => ({ x: row.x, year: row.year, y });
    if (isFiniteNumber(row.margin)) netMargin.push(at(row.margin));
    if (isFiniteNumber(row.ebitdaMargin)) ebitdaMargin.push(at(row.ebitdaMargin));
    const revGrowth = growthAt((item) => item.displayRevenue, index);
    if (isFiniteNumber(revGrowth)) revenueGrowth.push(at(revGrowth));
    const revGrowthTotal = growthAt((item) => item.revenue, index);
    if (isFiniteNumber(revGrowthTotal)) revenueGrowthTotal.push(at(revGrowthTotal));
    const revGrowthPerShare = growthAt((item) => item.growthRevenuePerShare, index);
    if (isFiniteNumber(revGrowthPerShare)) revenueGrowthPerShare.push(at(revGrowthPerShare));
    const shareChange = growthAt((item) => item.shares, index);
    if (isFiniteNumber(shareChange)) shareGrowth.push(at(shareChange));
    const fcfChange = growthAt((item) => item.fcf, index);
    if (isFiniteNumber(fcfChange)) fcfGrowth.push(at(fcfChange));
    // The true ratio, negative for net cash — an underlying line must not hide the sign.
    if (isFiniteNumber(row.netDebt) && isFiniteNumber(row.companyValue) && row.companyValue > 0) {
      netDebtToValue.push(at(row.netDebt / row.companyValue));
    }
    if (isFiniteNumber(row.roa)) roaPoints.push(at(row.roa));
    if (isFiniteNumber(row.roic)) roicPoints.push(at(row.roic));

    // Classic Rule of 40 (that year's Growth margin basis + YoY revenue
    // growth) on both revenue bases, for reference; not what the score plots.
    const r40Total = ruleOf40(row.growthMarginBasis, revGrowthTotal);
    if (isFiniteNumber(r40Total)) ruleOf40Total.push(at(r40Total));
    const r40PerShare = ruleOf40(row.growthMarginBasis, revGrowthPerShare);
    if (isFiniteNumber(r40PerShare)) ruleOf40PerShare.push(at(r40PerShare));
    // The Growth score, per year. Revenue and base revenue ride along for
    // the plotted average's non-positive-revenue cap.
    // A lender scores on net revenue with margin 0; the plotted growth and
    // Rule-of-40 reference lines above stay on reported revenue.
    const scored = lender
      ? yearGrowthScores(
        index,
        growthAt((item) => lenderNetAt(item.year), index),
        growthAt((item) => lenderNetPerShareByYear.get(item.year) ?? null, index),
        0,
      )
      : yearGrowthScores(index, revGrowthTotal, revGrowthPerShare, row.growthMarginBasis);
    const revenueContext = { revenue: peakRevenue[index], baseRevenue: index > 0 ? peakRevenue[index - 1] : null };
    if (isFiniteNumber(scored.total)) growthRawTotal.push({ ...at(scored.total), ...revenueContext });
    if (isFiniteNumber(scored.perShare)) growthRawPerShare.push({ ...at(scored.perShare), ...revenueContext });
  });

  // Quality: the screener's per-year engine over the raw statement rows. The
  // plotted line is the strict trailing 3-year average (null unless the last
  // 3 fiscal years are consecutive and each scores); the tooltip shows the
  // strict 5Y/10Y averages ending at that point. 0-100 arithmetic, identical
  // to houseScores.mjs; only the plotted value is divided by 100 (chart unit).
  const qualityCtx = qualityCtxOf(qualityInputs);
  const quality = [];
  qualityRows.forEach((row, i) => {
    const scored = computeYearQuality(qualityRows, i, qualityCtx);
    if (!isFiniteNumber(scored?.fraction)) return;
    const threeYear = averageQualityWindow(qualityRows, i, QUALITY_AVERAGE_WINDOWS.qualityScore, qualityCtx);
    const averages = [QUALITY_AVERAGE_WINDOWS.qualityScore5y, QUALITY_AVERAGE_WINDOWS.qualityScore10y]
      .map((n) => {
        const avg = averageQualityWindow(qualityRows, i, n, qualityCtx);
        return `${n}Y avg ${avg == null ? '—' : avg.toFixed(2)}`;
      });
    quality.push({
      x: row.x,
      year: row.year,
      y: isFiniteNumber(threeYear) ? threeYear / 100 : null,
      pillars: scored.pillars,
      raw: scored.fraction,
      detail: averages.join(' · '),
    });
  });

  // Forward estimates. Sampled from the UNDROPPED `revenue`/`ebitda` props —
  // the reported path above keeps using `dropAfterLastPrice` untouched. The
  // first forward year's growth denominator is the last reported FISCAL YEAR.
  const lastReported = years.length >= 2 ? rows[rows.length - 1] : null;
  const forwardYears = forwardFiscalYears(revenue, lastPriceTs, lastReported);
  if (forwardYears.length) {
    const forwardTotalRevenue = scaleTotalSeries(revenue, 'total', shares);
    const forwardPerShareRevenue = scaleTotalSeries(revenue, 'perShare', shares);
    // The margin term is HELD AT ITS LAST REPORTED LEVEL, not taken from the
    // EBITDA consensus. The reported years' `ebitda` is the provider's
    // GAAP-derived field (EBIT + D&A, stock comp still expensed) while
    // `ebitdaAvg` is an analyst ADJUSTED EBITDA that usually strips stock comp,
    // so splicing them steps the line by the size of that adjustment rather
    // than by anything economic — CRWD FY2026 reads 3.8% reported against 33.6%
    // on the consensus basis for the SAME year, and LLY FY2025 goes the other
    // way (48.6% vs 43.5%), so it is not a correctable constant either. Holding
    // the reported margin flat keeps the whole line on one basis and leaves the
    // forward movement to revenue growth, which is the term whose consensus IS
    // on the reported basis (consensus revenue ≈ reported revenue).
    // The plotted EBITDA-margin line holds its own last value; the score holds
    // the last reported year's Growth margin term (OCF for a REIT).
    const heldMargin = lastReported?.ebitdaMargin ?? null;
    const heldGrowthMargin = lastReported?.growthMarginBasis ?? null;
    let prior = lastReported;
    forwardYears.forEach(({ year, x }) => {
      const at = (y) => ({ x, year, y });
      const total = sampleNear(forwardTotalRevenue, x);
      const perShare = sampleNear(forwardPerShareRevenue, x);
      const gTotal = yoyGrowth(total, prior?.revenue);
      const gPerShare = yoyGrowth(perShare, prior?.perShareRevenue);
      if (isFiniteNumber(heldMargin)) ebitdaMargin.push(at(heldMargin));
      if (isFiniteNumber(gTotal)) revenueGrowthTotal.push(at(gTotal));
      if (isFiniteNumber(gPerShare)) revenueGrowthPerShare.push(at(gPerShare));
      const r40Total = ruleOf40(heldGrowthMargin, gTotal);
      if (isFiniteNumber(r40Total)) ruleOf40Total.push(at(r40Total));
      const r40PerShare = ruleOf40(heldGrowthMargin, gPerShare);
      if (isFiniteNumber(r40PerShare)) ruleOf40PerShare.push(at(r40PerShare));
      // A lender gets no forward Growth point: the consensus is gross revenue,
      // not net of interest expense.
      if (lender) {
        prior = { revenue: total, perShareRevenue: perShare };
        return;
      }
      // Same per-year rule as the reported years; the peak window runs over
      // reported and earlier forward revenue alike, while the rising-years
      // share stays at its last reported value.
      peakRevenue.push(total);
      peakYears.push(year);
      const scored = yearGrowthScores(peakRevenue.length - 1, gTotal, gPerShare, heldGrowthMargin);
      const revenueContext = { revenue: total, baseRevenue: prior?.revenue ?? null, forward: true };
      if (isFiniteNumber(scored.total)) growthRawTotal.push({ ...at(scored.total), ...revenueContext });
      if (isFiniteNumber(scored.perShare)) growthRawPerShare.push({ ...at(scored.perShare), ...revenueContext });
      prior = { revenue: total, perShareRevenue: perShare };
    });
  }

  // Plotted Growth lines (2026-11-01): each point is the strict trailing
  // 3-year average of the per-year scores ending there (null unless the last 3
  // scored years are consecutive) — the screener's growthScoreTotal/PerShare
  // rule, and the same shape as the Quality line. `raw` keeps the per-year
  // score. Reported points carry the 5Y/10Y tooltip averages of the reported
  // per-year scores only (the screener's growthScore*5y/10y basis); forward
  // points average across the reported/forward boundary so the line stays
  // continuous, and carry no `.detail`.
  const plotGrowth = (raw) => raw.map((point, i) => {
    const window = raw.slice(0, i + 1);
    const plotted = {
      x: point.x,
      year: point.year,
      y: trailingGrowthAverage(window, GROWTH_HEADLINE_WINDOW),
      raw: point.y,
    };
    if (!point.forward) plotted.detail = formatGrowthAverages(window);
    return plotted;
  });
  const growthScoreTotal = plotGrowth(growthRawTotal);
  const growthScorePerShare = plotGrowth(growthRawPerShare);

  return {
    years,
    forwardYears,
    netMargin,
    ebitdaMargin,
    revenueGrowth,
    revenueGrowthTotal,
    revenueGrowthPerShare,
    shareGrowth,
    netDebtToValue,
    fcfGrowth,
    roa: roaPoints,
    roic: roicPoints,
    quality,
    ruleOf40Total,
    ruleOf40PerShare,
    growthScoreTotal,
    growthScorePerShare,
  };
};
